/**
 * lifecycle.js — Sarabi OS · Orchestratore centrale di lifecycle eventi.
 *
 * Fornisce UNA sola fonte di verità per il "recovery" cross-device:
 *   - iOS/iPadOS Safari: pageshow (bfcache restore) + visibilitychange
 *   - Desktop: visibilitychange + focus + freeze/resume (Chrome Page Lifecycle)
 *   - Rete: online/offline (con debounce)
 *
 * L'obiettivo è che ogni hook (polling, audio, auth) NON registri i singoli
 * eventi browser ma sottoscriva un unico stream unificato:
 *
 *   const unsub = lifecycle.on("recovery", (ctx) => refetch());
 *
 * Eventi emessi:
 *   "recovery" → l'app è tornata operativa dopo una sospensione significativa
 *                (device wake, tab foreground dopo hide, bfcache restore,
 *                network online dopo offline, freeze→resume).
 *                ctx = { source, suspendedForMs, wasOffline }.
 *   "suspend"  → l'app sta entrando in stato non-operativo.
 *   "online"   → passaggio online (debounce già applicato in connectivity).
 *   "offline"  → passaggio offline (debounce già applicato).
 *   "visible" / "hidden" → visibility change grezzo (senza recovery inferring).
 *
 * Dedup / debounce:
 *   - Eventi ravvicinati (<300ms) vengono coalesciti in un solo "recovery".
 *   - "recovery" NON viene emesso se l'ultima sospensione è < 500ms (evita
 *     flicker su tab-switch veloci).
 *
 * Compatibilità PWA futura: il modulo è indipendente da Service Worker;
 * quando la PWA sarà installata, gli stessi eventi continueranno a funzionare
 * (pageshow/visibilitychange restano affidabili in standalone mode).
 */

const SUSPEND_THRESHOLD_MS = 500;   // sotto → non consideriamo "recovery"
const RECOVERY_DEBOUNCE_MS = 300;   // coalesce eventi ravvicinati

// Safety-net JS heartbeat: alcuni edge case iOS/iPadOS sospendono la tab
// SENZA emettere né visibilitychange né pageshow né focus. In quei casi
// nessun listener browser scatterebbe al ritorno.
// Contromisura: setInterval che misura il gap tra due tick consecutivi.
// Se il gap supera SAFETY_NET_GAP_MS il timer è stato realmente sospeso
// dal browser/OS → emettiamo un "recovery" con source="heartbeat-gap".
// Rimane comunque best-effort: se il browser droppa completamente il
// timer alla ripresa, il primo tick successivo al riavvio rileva il gap.
const HEARTBEAT_TICK_MS = 2000;
const SAFETY_NET_GAP_MS = 5000;

const _state = {
  online: (typeof navigator !== "undefined") ? navigator.onLine !== false : true,
  visible: (typeof document !== "undefined") ? !document.hidden : true,
  lastSuspendAt: 0,
  lastRecoveryAt: 0,
  recoveryTimer: null,
};

const _subscribers = {
  recovery: new Set(),
  suspend: new Set(),
  online: new Set(),
  offline: new Set(),
  visible: new Set(),
  hidden: new Set(),
};

function _emit(evt, payload) {
  const set = _subscribers[evt];
  if (!set) return;
  set.forEach((fn) => { try { fn(payload); } catch { /* silent */ } });
}

function _markSuspend(source) {
  if (_state.lastSuspendAt === 0) _state.lastSuspendAt = Date.now();
  _emit("suspend", { source });
}

function _scheduleRecovery(source) {
  const suspendedForMs = _state.lastSuspendAt
    ? Date.now() - _state.lastSuspendAt
    : 0;
  if (suspendedForMs < SUSPEND_THRESHOLD_MS && source !== "online") {
    // Micro-sospensione (tab-switch veloce): non emettere recovery.
    _state.lastSuspendAt = 0;
    return;
  }
  if (_state.recoveryTimer) clearTimeout(_state.recoveryTimer);
  _state.recoveryTimer = setTimeout(() => {
    _state.recoveryTimer = null;
    _state.lastRecoveryAt = Date.now();
    const wasOffline = source === "online";
    _emit("recovery", { source, suspendedForMs, wasOffline });
    _state.lastSuspendAt = 0;
  }, RECOVERY_DEBOUNCE_MS);
}

function _initListeners() {
  if (typeof window === "undefined") return;
  if (window.__sbLifecycleInit) return;
  window.__sbLifecycleInit = true;

  // visibilitychange
  document.addEventListener("visibilitychange", () => {
    const nowVisible = !document.hidden;
    _state.visible = nowVisible;
    if (nowVisible) {
      _emit("visible", {});
      _scheduleRecovery("visibility");
    } else {
      _emit("hidden", {});
      _markSuspend("visibility");
    }
  });

  // pageshow (bfcache restore su iOS Safari)
  window.addEventListener("pageshow", (e) => {
    if (e && e.persisted) {
      // Restore da bfcache: forza recovery a prescindere da lastSuspendAt.
      _state.lastSuspendAt = _state.lastSuspendAt || (Date.now() - 1000);
      _scheduleRecovery("pageshow");
    }
  });

  // pagehide
  window.addEventListener("pagehide", () => {
    _markSuspend("pagehide");
  });

  // focus / blur (segnale complementare)
  window.addEventListener("focus", () => {
    _state.visible = true;
    _scheduleRecovery("focus");
  });
  window.addEventListener("blur", () => {
    _markSuspend("blur");
  });

  // online / offline
  window.addEventListener("online", () => {
    _state.online = true;
    _emit("online", {});
    _scheduleRecovery("online");
  });
  window.addEventListener("offline", () => {
    _state.online = false;
    _emit("offline", {});
    _markSuspend("offline");
  });

  // Page Lifecycle API — Chrome desktop freeze/resume
  document.addEventListener("freeze", () => _markSuspend("freeze"));
  document.addEventListener("resume", () => _scheduleRecovery("resume"));

  // Safety-net JS heartbeat — cattura sospensioni "silenziose" (nessun
  // evento browser emesso). Vedi commento in cima al file.
  let _lastTick = Date.now();
  setInterval(() => {
    const now = Date.now();
    const gap = now - _lastTick;
    _lastTick = now;
    if (gap > SAFETY_NET_GAP_MS) {
      // Il timer è stato sospeso: consideralo come suspend appena rilevato.
      _state.lastSuspendAt = _state.lastSuspendAt || (now - gap);
      _scheduleRecovery("heartbeat-gap");
    }
  }, HEARTBEAT_TICK_MS);
}

_initListeners();

/**
 * Sottoscrivi un evento lifecycle.
 * Ritorna una funzione di unsubscribe.
 *
 *   const off = lifecycle.on("recovery", (ctx) => refetch());
 *   // ... later
 *   off();
 */
function on(evt, fn) {
  const set = _subscribers[evt];
  if (!set || typeof fn !== "function") return () => {};
  set.add(fn);
  return () => set.delete(fn);
}

function getState() {
  return {
    online: _state.online,
    visible: _state.visible,
    lastSuspendAt: _state.lastSuspendAt,
    lastRecoveryAt: _state.lastRecoveryAt,
  };
}

// Test helper: forza un evento (usato SOLO in unit test).
function __emitForTest(evt, payload) { _emit(evt, payload); }

export const lifecycle = { on, getState, __emitForTest };
